import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ErrorCode } from '@siteflow/shared';
import { toast } from 'sonner';
import { AuthError } from '@/components/auth/auth-error';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError, messageForError } from '@/lib/api-client';
import { authApi } from '@/lib/api/auth';
import { setLastOrgId, setSession, useAuth } from '@/lib/auth-store';

const schema = z.object({
  name: z.string().max(120).optional(),
  password: z
    .string()
    .max(200)
    .optional()
    .refine(
      (value) => !value || value.length >= 10,
      'Use at least 10 characters, or leave blank if you already have an account',
    ),
});

type Values = z.infer<typeof schema>;

function AcceptInvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { status } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);

  const alreadySignedIn = status === 'authenticated';

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', password: '' },
  });

  async function onSubmit(values: Values) {
    setFormError(null);
    try {
      const result = await authApi.acceptInvitation(token, {
        ...(values.name ? { name: values.name } : {}),
        ...(values.password ? { password: values.password } : {}),
      });

      setSession({
        accessToken: result.accessToken,
        user: result.user,
        organisations: result.organisations,
      });

      const target = result.organisations[0];
      toast.success('Invitation accepted');

      if (!target) {
        void navigate({ to: '/onboarding' });
        return;
      }
      setLastOrgId(target.id);
      void navigate({ to: '/app/$orgId', params: { orgId: target.id } });
    } catch (error) {
      if (ApiError.is(error) && error.code === ErrorCode.RATE_LIMITED) {
        setFormError('Too many attempts. Wait a moment before trying again.');
        return;
      }
      setFormError(messageForError(error, 'We could not accept that invitation.'));
    }
  }

  const pending = form.formState.isSubmitting;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <span className="section-label">Invitation</span>
        <h1 className="text-title font-semibold tracking-tight text-ink">Join the organisation</h1>
        <p className="text-data leading-relaxed text-ink-muted">
          {alreadySignedIn
            ? 'You are signed in — accepting will add this organisation to your account.'
            : 'Set a name and password to finish creating your account. Already have one? Leave both blank and sign in first.'}
        </p>
      </header>

      <Form {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <AuthError message={formError} />

          {!alreadySignedIn ? (
            <>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your name</FormLabel>
                    <FormControl>
                      <Input autoComplete="name" disabled={pending} {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        disabled={pending}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      At least 10 characters. Leave blank if you are already a SiteFlow user.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          ) : null}

          <Button type="submit" size="lg" disabled={pending} className="w-full">
            {pending ? 'Accepting…' : 'Accept invitation'}
          </Button>
        </form>
      </Form>

      <p className="border-t border-hairline pt-4 text-data text-ink-muted">
        Wrong account?{' '}
        <Link to="/login" className="text-accent hover:underline underline-offset-4">
          Sign in as someone else
        </Link>
      </p>
    </div>
  );
}

export const Route = createFileRoute('/(auth)/invite/$token')({
  component: AcceptInvitePage,
});
